/**
 * Game-over screen model: the finished run, the state of its leaderboard
 * submission, and what the screen should say about both. Pure — i18n keys are
 * returned (literal strings, so test/i18n.test.ts can find them), never text.
 */
import { ANTICHEAT, type Locale, type RunSummary } from '@dropstack/core';
import type { PlayerProfile, SessionTicket, SubmitResult } from '../../contracts.ts';
import { formatInteger, formatPercent, formatSeconds } from './format.ts';
import { isOnBoards, rankChips, type RankChip } from './leaderboard.ts';

/** How the submission of a finished run is going. */
export type SubmitState =
  | { status: 'skipped' }
  | { status: 'pending' }
  | { status: 'done'; result: SubmitResult }
  /** submitRun threw (a bug or an unexpected server answer). */
  | { status: 'failed' };

export interface FinishedRun {
  /** The router's run number (unique per page load). */
  run: number;
  summary: RunSummary;
  ticket: SessionTicket;
  quit: boolean;
  submit: SubmitState;
}

/** Runs without a single answer aren't worth a round trip (and can't score). */
export function shouldSubmit(summary: RunSummary): boolean {
  return summary.problems > 0;
}

/** Cheerful headline for the result (accuracy-based; a quit run just says it ended). */
export function headlineKey(summary: RunSummary, quit: boolean): string {
  if (summary.problems === 0) return 'shell.goHeadlineEmpty';
  if (quit) return 'shell.goHeadlineQuit';
  if (summary.accuracy >= 0.9) return 'shell.goHeadlineSuper';
  if (summary.accuracy >= 0.7) return 'shell.goHeadlineGreat';
  return 'shell.goHeadlineNice';
}

export interface StatTile {
  id: 'accuracy' | 'time' | 'levels' | 'reached' | 'layers' | 'streak';
  labelKey: string;
  value: string;
  /** Optional small print under the value. */
  detail?: { key: string; params: Record<string, string | number> };
}

/** The stat tiles, in display order. */
export function gameOverStats(summary: RunSummary, locale: Locale): StatTile[] {
  const answered = summary.problems > 0;
  return [
    {
      id: 'accuracy',
      labelKey: 'shell.goAccuracy',
      value: answered ? formatPercent(summary.accuracy) : '–',
      detail: { key: 'shell.goCorrectOf', params: { correct: summary.correct, total: summary.problems } },
    },
    {
      id: 'time',
      labelKey: 'shell.goMedianTime',
      value: answered ? formatSeconds(summary.medianResponseMs, locale) : '–',
    },
    {
      id: 'levels',
      labelKey: 'shell.goLevels',
      value: `${summary.levelStart} → ${summary.levelEnd}`,
    },
    { id: 'reached', labelKey: 'shell.goReached', value: String(summary.levelReached) },
    { id: 'layers', labelKey: 'shell.goLayers', value: formatInteger(summary.layersBroken) },
    { id: 'streak', labelKey: 'shell.goStreak', value: formatInteger(summary.bestStreak) },
  ];
}

/** What the leaderboard part of the game-over screen shows. */
export type SubmitView =
  | { kind: 'no-answers' }
  | { kind: 'saving' }
  /** On the boards, with a rank per scope (may be empty while ranks refresh). */
  | { kind: 'ranked'; chips: RankChip[] }
  /** Saved, but the player isn't on the boards: invite them (quietly once they said "not now"). */
  | { kind: 'join'; quiet: boolean; onboarded: boolean }
  /**
   * The account's first completed run (GAME_DESIGN § 5): saved and the level is
   * set, but it is never ranked. `invite` = the player isn't on the boards yet,
   * so joining now lets the next run count.
   */
  | { kind: 'placement'; invite: boolean }
  /**
   * A "More time" run (GAME_DESIGN § 4.1): saved and the level moves, but it
   * never goes on a leaderboard — said kindly, with no invite to join.
   */
  | { kind: 'relaxed' }
  /** Accepted but held back for a check. */
  | { kind: 'review' }
  /** Played or sent without a connection: kept on this device only. */
  | { kind: 'offline'; canRetry: boolean }
  /** The run couldn't be ranked (expired/used session, failed checks). */
  | { kind: 'refused'; messageKey: string }
  | { kind: 'error'; canRetry: boolean };

export interface SubmitViewInput {
  run: FinishedRun;
  profile: PlayerProfile | null;
  /** The player said "not now" to joining before. */
  joinDismissed: boolean;
  /** Wall clock (ms), for ticket expiry. */
  now: number;
  locale: Locale;
}

export function submitView({ run, profile, joinDismissed, now, locale }: SubmitViewInput): SubmitView {
  const { submit } = run;
  switch (submit.status) {
    case 'skipped':
      return { kind: 'no-answers' };
    case 'pending':
      return { kind: 'saving' };
    case 'failed':
      return { kind: 'error', canRetry: canResubmit(run.ticket, submit, now) };
    case 'done':
      break;
  }
  const { result } = submit;
  if (!result.accepted) {
    if (result.reason === 'offline' || result.reason === 'network') {
      return { kind: 'offline', canRetry: canResubmit(run.ticket, submit, now) };
    }
    return { kind: 'refused', messageKey: refusalKey(result.reason) };
  }
  // Relaxed runs are never ranked, so "held back for a check" or "join" would only confuse.
  if (result.relaxed) return { kind: 'relaxed' };
  if (result.flagged) return { kind: 'review' };
  if (result.placement) return { kind: 'placement', invite: !isOnBoards(profile) };
  if (!isOnBoards(profile)) return { kind: 'join', quiet: joinDismissed, onboarded: Boolean(profile?.onboarded) };
  return { kind: 'ranked', chips: rankChips(result.ranks, profile, locale) };
}

/** Message for a run the backend didn't accept. */
export function refusalKey(reason: string | undefined): string {
  switch (reason) {
    case 'session-expired':
      return 'shell.goRefusedExpired';
    case 'session-used':
      return 'shell.goRefusedUsed';
    case 'rules-version':
      return 'shell.goRefusedVersion';
    default:
      return 'shell.goRefusedCheck';
  }
}

/**
 * Whether sending the run again can still work: only after a connection
 * problem, only with a real (server-signed) ticket, and only while that
 * single-use ticket is inside its time-to-live.
 */
export function canResubmit(ticket: SessionTicket, submit: SubmitState, now: number): boolean {
  if (ticket.offline || !ticket.token) return false;
  const retryable = submit.status === 'failed' || (submit.status === 'done' && submit.result.reason === 'network');
  return retryable && now - ticket.issuedAt < ANTICHEAT.SESSION_TTL_MS;
}

/**
 * The result shown for a run started with a locally made offline ticket (the
 * server never issued it, so there is nothing to send). The caller records
 * `summary.savedRating` locally so the next run still warm-starts.
 * `reason` is 'rules-version' when the ticket replaced a server ticket for
 * newer game rules (session.ts § onRulesMismatch): the screen then asks the
 * player to reload instead of saying they were offline.
 */
export function offlineResult(
  summary: RunSummary,
  reason: 'offline' | 'rules-version' = 'offline',
  relaxed = false,
): SubmitResult {
  return {
    accepted: false,
    flagged: false,
    runId: null,
    raw: summary.raw,
    rated: summary.rated,
    savedRating: summary.savedRating,
    ranks: {},
    personalBest: false,
    placement: false,
    relaxed,
    reason,
  };
}

/** The scores to show: the backend's recomputed values once accepted, else the run's own. */
export function shownScores(run: FinishedRun): { raw: number; rated: number } {
  if (run.submit.status === 'done' && run.submit.result.accepted) {
    return { raw: run.submit.result.raw, rated: run.submit.result.rated };
  }
  return { raw: run.summary.raw, rated: run.summary.rated };
}

/** True when this run set a new personal best (confirmed by the backend; never for a placement run). */
export function isPersonalBest(run: FinishedRun): boolean {
  const { submit } = run;
  if (submit.status !== 'done') return false;
  const { result } = submit;
  return result.accepted && !result.placement && !result.relaxed && result.personalBest;
}

/**
 * Whether the game-over screen should look up the run's ranks: it was stored,
 * counts for the boards (not flagged, placement or relaxed), the player is on
 * the boards now (e.g. they just joined), and no ranks came back with it.
 */
export function needsRankRefresh(run: FinishedRun, profile: PlayerProfile | null): boolean {
  const { submit } = run;
  if (submit.status !== 'done' || !isOnBoards(profile)) return false;
  const { result } = submit;
  return result.accepted && !result.flagged && !result.placement && !result.relaxed && Object.keys(result.ranks).length === 0;
}
